import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import ButtonSecondaryMedium from '../common/ButtonSecondary/ButtonSecondaryMedium';
import SwitchComponent from '../common/SwitchComponent/SwitchComponent';
import { openMessageError } from '../common/Message/openMessageError';
import { openMessageSuccess } from '../common/Message/openMessageSuccess';
import {
  addView,
  applyViewState,
  DIM_LAYER_GROUPS,
  removeView,
  renameView,
  setLayerVisible,
  setPreset,
  updateView,
} from '../../store/reducers/dimension.reducer';
import {
  applyCameraView,
  captureCameraView,
  captureDimState,
  computeStandardView,
  modulesWorldBox,
  STANDARD_VIEWS,
} from '../../utils/dimension/dimensionViews';
import { cn } from '../../utils/string/cn.utils';
import { Field, GROUP_LABELS, Segmented } from './DimensionControls';
import { buildGroupIndex } from './DimensionOverlay';
import { exportCurrentView } from './exportView';

const STANDARD_VIEW_LABELS = {
  front: 'Front',
  back: 'Back',
  left: 'Left',
  right: 'Right',
  top: 'Top',
};

const Title = ({ children }) => (
  <span className="text-label-small-emphasized uppercase tracking-wide text-black-medium">
    {children}
  </span>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    aria-pressed={!!active}
    onClick={onClick}
    className={cn(
      'h-[28px] px-[10px] rounded-[100px] border border-solid cursor-pointer text-body-small',
      active
        ? 'bg-primary-primaryFill border-outline-brand-primary text-primary-primary font-semibold'
        : 'bg-fills-white border-outline-default-primary text-black-medium hover:text-black-high'
    )}
  >
    {children}
  </button>
);

/**
 * Khung "Lớp & góc nhìn" của công cụ DIM:
 *  - preset hiển thị (Tổng thể / Tủ trên / Tủ dưới / Chi tiết / Phụ kiện) và bật
 *    tắt từng lớp, để không phải hiện mọi con số cùng lúc
 *  - góc nhìn chuẩn (trước / sau / trái / phải / trên) và góc nhìn đã lưu
 *  - xuất ảnh góc nhìn hiện tại kèm đường DIM
 */
const DimensionViewsPanel = ({ sceneManager }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { layers, preset, items, views } = useSelector(
    state => state.dimension
  );
  const [format, setFormat] = useState('image/png');
  const [withDim, setWithDim] = useState(true);
  const [busy, setBusy] = useState(false);

  const countOf = group => items.filter(i => i.group === group).length;

  const goStandard = name => {
    const groups = [...buildGroupIndex(sceneManager).values()];
    const box =
      modulesWorldBox(groups) ??
      new THREE.Box3(new THREE.Vector3(-1, 0, -1), new THREE.Vector3(1, 2, 1));
    const view = computeStandardView(name, box, sceneManager?.camera?.fov);
    if (view) applyCameraView(sceneManager, view);
  };

  const saveView = () => {
    const current = captureCameraView(sceneManager);
    if (!current) return;
    dispatch(
      addView({
        name: `${t('View')} ${views.length + 1}`,
        ...current,
        dim: captureDimState({ layers, preset, items }),
      })
    );
  };

  // Mở một view đã lưu: bộ DIM hiện + góc camera
  const openView = view => {
    dispatch(applyViewState(view.id));
    applyCameraView(sceneManager, view);
  };

  // Ghi đè view bằng góc nhìn và bộ DIM đang hiện
  const overwriteView = view => {
    const current = captureCameraView(sceneManager);
    if (!current) return;
    dispatch(
      updateView({
        id: view.id,
        ...current,
        dim: captureDimState({ layers, preset, items }),
      })
    );
    openMessageSuccess({ content: t('View updated') });
  };

  const doExport = async () => {
    setBusy(true);
    try {
      await exportCurrentView({
        sceneManager,
        dispatch,
        format,
        includeDimensions: withDim,
      });
      openMessageSuccess({ content: t('Image exported') });
    } catch (error) {
      openMessageError({ content: t('Could not export the image') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex flex-col gap-12 p-12 w-[340px] max-h-[60vh] overflow-y-auto rounded-[8px] bg-[#fff] shadow-dropshadow8"
      // Gõ tên góc nhìn không được kích hoạt phím tắt của designer
      onKeyDown={e => e.stopPropagation()}
    >
      <div className="flex flex-col gap-8">
        <Title>{t('Show')}</Title>
        <div className="flex flex-wrap gap-6">
          <Chip
            active={preset === 'all'}
            onClick={() => dispatch(setPreset('all'))}
          >
            {t('All')}
          </Chip>
          {DIM_LAYER_GROUPS.map(group => (
            <Chip
              key={group}
              active={preset === group}
              onClick={() => dispatch(setPreset(group))}
            >
              {t(GROUP_LABELS[group])}
            </Chip>
          ))}
        </div>
        {DIM_LAYER_GROUPS.map(group => (
          <Field
            key={group}
            label={`${t(GROUP_LABELS[group])} (${countOf(group)})`}
            hintPlacement="right"
          >
            <SwitchComponent
              size="small"
              id={`dim-layer-${group}`}
              value={layers[group]}
              onChange={visible =>
                dispatch(setLayerVisible({ group, visible }))
              }
            />
          </Field>
        ))}
      </div>

      <div className="flex flex-col gap-8">
        <Title>{t('Camera views')}</Title>
        <div className="flex flex-wrap gap-6">
          {Object.keys(STANDARD_VIEWS).map(name => (
            <Chip key={name} onClick={() => goStandard(name)}>
              {t(STANDARD_VIEW_LABELS[name])}
            </Chip>
          ))}
        </div>
        {views.map(view => (
          <div key={view.id} className="flex items-center gap-6">
            <input
              aria-label={t('View name')}
              value={view.name}
              onChange={e =>
                dispatch(renameView({ id: view.id, name: e.target.value }))
              }
              className="flex-1 min-w-0 h-[28px] px-[8px] rounded-[6px] bg-fills-white border border-solid border-outline-default-primary text-body-small outline-none focus:border-outline-brand-primary"
            />
            <button
              type="button"
              className="h-[28px] px-[8px] rounded-[6px] border-0 bg-primary-primaryFill text-primary-primary text-body-small cursor-pointer"
              onClick={() => openView(view)}
            >
              {t('Go')}
            </button>
            <button
              type="button"
              aria-label={`${t('Update view')} ${view.name}`}
              title={t('DIM_HINT_UPDATE_VIEW')}
              className="h-[28px] px-[8px] rounded-[6px] border-0 bg-fills-secondary text-black-medium text-body-small cursor-pointer"
              onClick={() => overwriteView(view)}
            >
              {t('Update view')}
            </button>
            <button
              type="button"
              aria-label={`${t('Delete')} ${view.name}`}
              className="h-[28px] px-[8px] rounded-[6px] border-0 bg-trans text-Accents-Red text-body-small cursor-pointer"
              onClick={() => dispatch(removeView(view.id))}
            >
              {t('Delete')}
            </button>
          </div>
        ))}
        <ButtonSecondaryMedium
          title={t('Save current view')}
          onClick={saveView}
        />
      </div>

      <div className="flex flex-col gap-8">
        <Title>{t('Export')}</Title>
        <Field label={t('Format')} hintPlacement="right">
          <Segmented
            value={format}
            onChange={setFormat}
            options={[
              { value: 'image/png', label: 'PNG' },
              { value: 'image/jpeg', label: 'JPG' },
            ]}
          />
        </Field>
        <Field
          label={t('Include DIM')}
          hint={t('DIM_HINT_EXPORT_DIM')}
          hintPlacement="right"
        >
          <SwitchComponent
            size="small"
            id="dim-export-with-dim"
            value={withDim}
            onChange={setWithDim}
          />
        </Field>
        <ButtonSecondaryMedium
          title={t('Export with these settings')}
          disabled={busy}
          onClick={doExport}
        />
      </div>
    </div>
  );
};

export default DimensionViewsPanel;
